// Search-results map, reusing the marker store and fetch helpers built for the
// property map. Desktop shows it for `view=map`; mobile keeps it on every page
// behind the `#map` hash that `search-map-overlay` answers.
//
// The element is `data-turbo-permanent`, so the gmap instance survives filter
// changes and map/list toggles. Same-pathname renders morph and step over it,
// leaving this controller connected; a render that replaces the body transplants
// the element and Stimulus builds a fresh controller. Either way the instance
// and the scope it was last fetched for live on the element rather than on the
// controller.
;(() => {
  const CONFIG_TAG_ID = "search-map-data"
  const SCROLL_DELAY_MS = 300

  function focusOfferCard(id) {
    document
      .getElementById(`google-maps-offer-${id}`)
      ?.dispatchEvent(new CustomEvent("hb:focus"))
  }

  function boundingBoxToBounds(box) {
    return new google.maps.LatLngBounds(
      { lat: box["sw-lat"], lng: box["sw-lng"] },
      { lat: box["ne-lat"], lng: box["ne-lng"] },
    )
  }

  function markersUrlWith(baseUrl, params) {
    const url = new URL(baseUrl, window.location.origin)
    for (const [key, value] of Object.entries(params)) {
      if (value != null) url.searchParams.set(key, value)
    }
    return url.pathname + "?" + url.searchParams.toString()
  }

  stimulusApp.register(
    "search-map",
    class extends Stimulus.Controller {
      static targets = ["canvas"]
      static values = { mobile: Boolean }
      static outlets = ["filters-form"]

      connect() {
        this.#sync()
      }

      // A filter, sort, tag or map/list change is a `replace` visit to the same
      // pathname, which Turbo renders as a page refresh — a morph. Morph steps
      // over `data-turbo-permanent` elements, so this controller is never
      // disconnected and `connect` never runs for those renders.
      refresh() {
        this.#sync()
      }

      // The map being revealed: boot it if this is the first open, catch it up
      // if the page changed while it was hidden, otherwise just re-measure — it
      // was sized against a `hidden` container.
      show() {
        if (!this.element.searchMap) {
          this.#boot(this.#readConfig())
        } else if (this.#state.stale) {
          this.#state.stale = false
          this.#reuse(this.#readConfig())
        } else if (this.#state.gmap) {
          google.maps.event.trigger(this.#state.gmap, "resize")
        }
      }

      disconnect() {
        clearTimeout(this.element.searchMap?.scrollTimeout)
      }

      scopeCounts() {
        if (!this.hasFiltersFormOutlet) return

        const bounds =
          document.body.classList.contains("google-maps-open") &&
          this.#state?.gmap?.getBounds()
        this.filtersFormOutlet.scopeCounts(
          bounds ? HB.mapHelpers.boundsToBoundingBox(bounds) : null,
        )
      }

      // Mobile only: a map background tap clears the selection, hiding the
      // offer card. Marker taps stop their own propagation in `#selectMarker`.
      clearSelection() {
        const selectedId = HB.mapHelpers.selectedMarkerId()
        if (selectedId == null) return

        HB.mapHelpers.deselectMarkerById(selectedId)
        document.dispatchEvent(new CustomEvent("search-map:clear-selection"))
      }

      #sync() {
        const config = this.#readConfig()
        if (!config) return

        // Both variants carry the config on every page but pay for Google Maps
        // only once the map is first opened: mobile's `search-map-overlay` nudges
        // `refresh` and the desktop mode toggle nudges `show` when they open it.
        // Desktop then leaves a closed map alone, only noting that it fell
        // behind for `show` to catch up on the way in, while mobile keeps
        // re-scoping a booted map under the closed overlay, which nudges
        // `refresh` only once per page.
        if (
          !document.body.classList.contains("google-maps-open") &&
          (!this.mobileValue || !this.element.searchMap)
        ) {
          if (this.element.searchMap) this.#state.stale = true
          return
        }

        if (this.element.searchMap) {
          this.#reuse(config)
        } else {
          this.#boot(config)
        }
      }

      #readConfig() {
        if (!document.getElementById(CONFIG_TAG_ID)) return null

        return HB.mapHelpers.buildConfig(CONFIG_TAG_ID)
      }

      get #state() {
        return this.element.searchMap
      }

      #boot(config) {
        this.element.searchMap = {
          config,
          gmap: null,
          markersUrl: config.markers_url,
          markersLoaded: false,
          stale: false,
          prev: { bounds: null, zoom: null },
          abortController: null,
          scrollTimeout: null,
          snazzyLoaded: null,
        }

        HB.mapHelpers
          .loadGoogleMaps(config.js_libs.google_maps_url)
          .then(() => {
            this.#state.snazzyLoaded = HB.loadJS(
              config.js_libs.snazzy_info_window_path,
              "module",
            )
            this.#createMap()
          })
      }

      #createMap() {
        const gmap = new google.maps.Map(
          this.canvasTarget,
          $.extend(
            true,
            {},
            HB.mapHelpers.defaultMapOptions(),
            this.#state.config.map_options,
          ),
        )
        this.#state.gmap = gmap

        // Fit bounds only once the map is ready: it can be initialised from
        // options, so it needs to finish that before the camera is moved.
        google.maps.event.addListenerOnce(gmap, "idle", () => {
          this.#fitInitialBounds()
          this.#addRegionMarker()
        })

        document.dispatchEvent(
          new CustomEvent("search-map:mount", { detail: { gmap } }),
        )

        // Compensates the fixed map container while the page is scrolled
        // horizontally at browser zoom levels above 100%.
        if (!this.mobileValue) $(this.canvasTarget).fixer_zoom_compensator()
      }

      #reuse(config) {
        const scopeChanged = config.markers_url !== this.#state.markersUrl
        this.#state.config = config
        this.#state.markersUrl = config.markers_url

        if (!this.#state.gmap) return // still booting; #createMap picks it up

        // The transplant can leave the map sized against the old layout.
        google.maps.event.trigger(this.#state.gmap, "resize")

        if (scopeChanged) {
          HB.mapHelpers.removeAllMarkers()
          this.#state.prev = { bounds: null, zoom: null }
          this.#fetchMarkers({ incremental: false })
        } else if (this.#state.markersLoaded) {
          // The cards frame is rendered fresh on every page render, so it has to
          // be refilled even when the markers themselves are still valid.
          HB.mapHelpers.fetchOffers(false, null, config.offers_url)
        }
      }

      async #fitInitialBounds() {
        const box =
          this.#state.config.initial_bounds ??
          (await this.#requestBoundingBox())
        if (!box) return

        this.#state.gmap.addListener("idle", () => {
          this.#fetchMarkers({ incremental: true })
          this.scopeCounts()
        })
        this.#state.gmap.fitBounds(boundingBoxToBounds(box), 0)
      }

      async #requestBoundingBox({ retry = true } = {}) {
        const response = await RailsRequest.get(
          this.#state.config.bounding_box_url,
          { responseKind: "json" },
        )

        // `SearchMapMarkersController` heads 404 when the prepared search behind
        // the results has expired — a reload rebuilds it.
        if (response.statusCode === 404) {
          window.location.reload()
          return null
        }
        if (response.statusCode === 403 && retry) {
          await HB.proofOfWork.solve((await response.json).proof_of_work)
          return this.#requestBoundingBox({ retry: false })
        }
        if (!response.ok) {
          console.error("Search map bounding box failed", response.statusCode)
          return null
        }

        return (await response.json)["bounding-box"]
      }

      async #fetchMarkers({ incremental, retry = true }) {
        const { config } = this.#state
        const { params, nextPrev } = HB.mapHelpers.buildFetchMarkersParams(
          this.#state.gmap,
          this.#state.prev,
          {
            clusteringThreshold: config.clusteringThreshold,
            recommendedPositionStatus: config.recommended_position_status,
          },
        )
        this.#state.prev = nextPrev
        if (!params) return // map not ready yet

        // A pan that outruns its own request would otherwise paint stale markers.
        this.#state.abortController?.abort()
        this.#state.abortController = new AbortController()

        try {
          const response = await RailsRequest.get(
            markersUrlWith(config.markers_url, params),
            {
              responseKind: "json",
              signal: this.#state.abortController.signal,
            },
          )
          if (response.statusCode === 403) {
            if (!retry) {
              console.error("Proof of work did not unlock the map markers")
              return
            }

            await HB.proofOfWork.solve((await response.json).proof_of_work)
            return this.#fetchMarkers({ incremental, retry: false })
          }

          HB.mapHelpers.processFetchedMarkers({
            incremental,
            dataToMarkerFn: this.#buildDataToMarker(),
            offersUrl: config.offers_url,
          })(await response.json)
          this.#state.markersLoaded = true
        } catch (error) {
          if (error.name !== "AbortError") {
            console.error("Search map markers failed", error)
          }
        }
      }

      // Touch taps synthesize mouse events, so the hover handlers would fight
      // the tap selection.
      #buildDataToMarker() {
        return HB.mapHelpers.buildDataToMarker({
          gmap: this.#state.gmap,
          loadingHtml: this.#state.config.marker_loading_html,
          markerAriaLabel: this.#state.config.marker_aria_label,
          onClickMarker: this.#selectMarker,
          ...(this.mobileValue
            ? {}
            : {
                onMouseEnterMarker: this.#previewMarker,
                onMouseLeaveMarker: this.#unpreviewMarker,
              }),
        })
      }

      async #addRegionMarker() {
        const region = this.#state.config.region
        if (!region) return

        await this.#state.snazzyLoaded

        const marker = new google.maps.Marker({
          position: { lat: region.latitude, lng: region.longitude },
          icon: {
            url: this.#state.config.pin_urls.region,
            scaledSize: new google.maps.Size(45, 45),
          },
          map: this.#state.gmap,
        })

        new SnazzyInfoWindow({
          marker,
          closeOnMapClick: true,
          content: region.card_html,
          panOnOpen: true,
          shadow: false,
          pointer: false,
          showCloseButton: false,
        }).open()
      }

      #selectMarker = (marker, event) => {
        if (HB.mapHelpers.selectedMarkerId() === marker.id) {
          if (event.target.closest("a[href]")) {
            Activity.capture("search-map property open", {
              property_id: marker.id,
              from: "marker",
            })
          }
          return
        }

        event.stopPropagation()
        event.preventDefault()
        HB.mapHelpers.selectMarkerById(marker.id)
        focusOfferCard(marker.id)
        Activity.capture("search-map marker select", { property_id: marker.id })
      }

      #previewMarker = (marker) => {
        clearTimeout(this.#state.scrollTimeout)
        this.#state.scrollTimeout = setTimeout(
          focusOfferCard,
          SCROLL_DELAY_MS,
          marker.id,
        )
        HB.mapHelpers.selectMarkerById(marker.id)
      }

      #unpreviewMarker = (marker) => {
        clearTimeout(this.#state.scrollTimeout)
        HB.mapHelpers.deselectMarkerById(marker.id)
      }
    },
  )
})()
